/**
 * Product Card Custom Element
 * Handles interactions for product cards including quick add, quick view,
 * variant swatches, and image hover effects.
 * Designed for Light DOM usage in Shopify themes.
 */

// Helper to format money (fallback if Shopify.formatMoney is not available)
function formatMoney(cents, currency = '$') {
  return `${currency}${(cents / 100).toFixed(2)}`;
}

class ProductCard extends HTMLElement {
  // Private fields
  #hoverMatchMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
  #variants = null;
  #currentVariantId = null;
  #intersectionObserver = null;
  #debounceSwatchClick = null;
  #activeImageSrc = null;
  
  // DOM Elements cache
  #elements = {
    media: null,
    primaryImage: null,
    secondaryImage: null,
    price: null,
    badge: null,
    quickAddBtn: null,
    swatchContainer: null,
    swatches: null
  };

  constructor() {
    super();
    // Bind handleEvent so it can be used for all event listeners
    this.handleEvent = this.handleEvent.bind(this);
    
    // Debounce the swatch click to prevent rapid firing
    this.#debounceSwatchClick = this.#debounce(this.#handleSwatchClick.bind(this), 100);
  }

  connectedCallback() {
    this.#initElements();
    this.#initData();
    this.#setupEventListeners();
    
    // Defer non-critical setup (like IntersectionObserver for animations)
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => this.#setupIntersectionObserver());
    } else {
      setTimeout(() => this.#setupIntersectionObserver(), 0);
    }
  }

  disconnectedCallback() {
    // Clean up event listeners to prevent memory leaks
    this.removeEventListener('click', this.handleEvent);
    this.removeEventListener('mouseenter', this.handleEvent);
    this.removeEventListener('mouseleave', this.handleEvent);
    
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }
  }

  #initElements() {
    this.#elements.media = this.querySelector('.product-card__media');
    this.#elements.primaryImage = this.querySelector('[data-media-primary]');
    this.#elements.secondaryImage = this.querySelector('[data-media-secondary]');
    this.#elements.price = this.querySelector('.product-card__price');
    this.#elements.badge = this.querySelector('.product-card__badge');
    this.#elements.quickAddBtn = this.querySelector('.product-card__quick-add');
    this.#elements.swatches = Array.from(this.querySelectorAll('.product-card__swatch'));

    if (this.#elements.primaryImage) {
      this.#activeImageSrc = this.#elements.primaryImage.src;
    }
  }

  #initData() {
    // Read data attributes from the host element
    this.productId = this.dataset.productId;
    this.productUrl = this.dataset.productUrl;
    this.productTitle = this.dataset.productTitle;
    this.isAvailable = this.dataset.available === 'true';
    this.category = this.dataset.category || '';
    
    try {
      if (this.dataset.variantsJson) {
        this.#variants = JSON.parse(this.dataset.variantsJson);
      }
    } catch (e) {
      console.error('[ProductCard] Error parsing variants JSON', e);
      this.#variants = [];
    }

    if (this.#elements.quickAddBtn) {
      this.#currentVariantId = this.#elements.quickAddBtn.dataset.variantId || 
                               (this.#variants && this.#variants.length > 0 ? this.#variants[0].id : null);
    }
  }

  #setupEventListeners() {
    // Use event delegation on the card itself for click events
    this.addEventListener('click', this.handleEvent);

    // Setup hover effect only for devices that support hover
    if (this.#hoverMatchMedia.matches && this.#elements.secondaryImage && this.#elements.media) {
      // Use passive listeners for better scrolling performance
      this.addEventListener('mouseenter', this.handleEvent, { passive: true });
      this.addEventListener('mouseleave', this.handleEvent, { passive: true });
    }

    // Swatch hover preview: mouseenter/mouseleave don't bubble, so bind per swatch
    if (this.#hoverMatchMedia.matches) {
      this.#elements.swatches.forEach((swatch) => {
        swatch.addEventListener('mouseenter', () => this.#handleSwatchHover(swatch), { passive: true });
        swatch.addEventListener('mouseleave', () => this.#handleSwatchHoverEnd(), { passive: true });
      });
    }
  }

  /**
   * Main event handler leveraging the handleEvent interface
   * @param {Event} event 
   */
  handleEvent(event) {
    switch (event.type) {
      case 'click':
        this.#handleClick(event);
        break;
      case 'mouseenter':
        this.#handleMouseEnter();
        break;
      case 'mouseleave':
        this.#handleMouseLeave();
        break;
    }
  }

  #handleClick(event) {
    const quickAddBtn = event.target.closest('.product-card__quick-add');
    const quickViewBtn = event.target.closest('.product-card__quick-view');
    const swatchBtn = event.target.closest('.product-card__swatch');

    if (quickAddBtn && this.contains(quickAddBtn)) {
      event.preventDefault();
      this.#handleQuickAdd(quickAddBtn);
    } else if (quickViewBtn && this.contains(quickViewBtn)) {
      event.preventDefault();
      this.#handleQuickView();
    } else if (swatchBtn && this.contains(swatchBtn)) {
      event.preventDefault();
      // Route through debounced function
      this.#debounceSwatchClick(swatchBtn);
    }
  }

  #handleMouseEnter() {
    if (this.#elements.media) {
      // Use requestAnimationFrame for smooth class toggling
      requestAnimationFrame(() => {
        this.#elements.media.classList.add('product-card__media--hover-active');
      });
    }
  }

  #handleMouseLeave() {
    if (this.#elements.media) {
      requestAnimationFrame(() => {
        this.#elements.media.classList.remove('product-card__media--hover-active');
      });
    }
  }

  #handleSwatchClick(swatchBtn) {
    const variantId = swatchBtn.dataset.variantId;
    if (!variantId || !this.#variants) return;

    const variant = this.#variants.find(v => v.id.toString() === variantId.toString());
    if (!variant) return;

    this.#currentVariantId = variant.id;

    // 1. Update aria-pressed for accessibility
    this.#elements.swatches.forEach(swatch => {
      swatch.setAttribute('aria-pressed', swatch === swatchBtn ? 'true' : 'false');
    });

    // 2. Update Image
    if (variant.featured_image && this.#elements.primaryImage) {
      const src = variant.featured_image.src || variant.featured_image;
      this.#activeImageSrc = src;
      requestAnimationFrame(() => {
        this.#elements.primaryImage.src = src;
        if (this.#elements.primaryImage.srcset) {
          // Note: In a full implementation, you'd likely update the srcset properly with resized URLs.
          // For simplicity here, if there's a srcset, we set it to the new source.
          this.#elements.primaryImage.srcset = src;
        }
      });
    }

    // 3. Update Price display
    if (this.#elements.price && variant.price !== undefined) {
      const formattedPrice = window.Shopify && window.Shopify.formatMoney 
        ? window.Shopify.formatMoney(variant.price) 
        : formatMoney(variant.price);
      
      requestAnimationFrame(() => {
        this.#elements.price.textContent = formattedPrice;
      });
    }

    // 4. Update Quick Add Button state & availability badge
    if (this.#elements.quickAddBtn) {
      this.#elements.quickAddBtn.dataset.variantId = variant.id;
      
      if (!variant.available) {
        this.#elements.quickAddBtn.disabled = true;
        this.#elements.quickAddBtn.setAttribute('aria-disabled', 'true');
        // If there's an original text dataset, we use it, otherwise fallback
        this.#elements.quickAddBtn.textContent = this.#elements.quickAddBtn.dataset.soldOutText || 'Sold Out';
      } else {
        this.#elements.quickAddBtn.disabled = false;
        this.#elements.quickAddBtn.setAttribute('aria-disabled', 'false');
        this.#elements.quickAddBtn.textContent = this.#elements.quickAddBtn.dataset.defaultText || 'Add to Cart';
      }
    }
  }

  #handleSwatchHover(swatchBtn) {
    const variantId = swatchBtn.dataset.variantId;
    if (!variantId || !this.#variants || !this.#elements.primaryImage) return;

    const variant = this.#variants.find((v) => v.id.toString() === variantId.toString());
    if (!variant || !variant.featured_image) return;

    const src = variant.featured_image.src || variant.featured_image;
    requestAnimationFrame(() => {
      // Force the primary image visible even if the card's own hover
      // is currently showing the secondary image.
      if (this.#elements.media) {
        this.#elements.media.classList.add('product-card__media--swatch-preview');
      }
      this.#elements.primaryImage.src = src;
    });
  }

  #handleSwatchHoverEnd() {
    if (!this.#elements.primaryImage || !this.#activeImageSrc) return;

    requestAnimationFrame(() => {
      this.#elements.primaryImage.src = this.#activeImageSrc;
      if (this.#elements.media) {
        this.#elements.media.classList.remove('product-card__media--swatch-preview');
      }
    });
  }

  async #handleQuickAdd(button) {
    if (button.classList.contains('is-loading') || button.disabled) return;
    
    const variantId = this.#currentVariantId;
    if (!variantId) return;

    // Set Loading State
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1
          }],
          // Rendered drawer comes back with the add (see cart-drawer.js open())
          sections: ['cart-drawer'],
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const added = await response.json();

      // Handle Success state
      requestAnimationFrame(() => {
        button.classList.remove('is-loading');
        button.setAttribute('aria-busy', 'false');
        
        button.classList.add('is-success');
        
        // Save original text to restore later if not already saved
        if (!button.dataset.defaultText) {
          button.dataset.defaultText = button.textContent;
        }
        button.textContent = 'Added';
      });

      // Dispatch event for other components (like cart drawer) to react
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        bubbles: true,
        detail: {
          variantId: variantId,
          productTitle: this.productTitle,
          sections: added && added.sections
        }
      }));

      // Revert button state after 2 seconds
      setTimeout(() => {
        requestAnimationFrame(() => {
          button.classList.remove('is-success');
          if (button.dataset.defaultText) {
            button.textContent = button.dataset.defaultText;
          }
        });
      }, 2000);

    } catch (error) {
      console.error('[ProductCard] Error adding item to cart', error);
      
      // Handle Error state
      requestAnimationFrame(() => {
        button.classList.remove('is-loading');
        button.setAttribute('aria-busy', 'false');
        button.classList.add('is-error');
      });
      
      setTimeout(() => {
        requestAnimationFrame(() => {
          button.classList.remove('is-error');
        });
      }, 2000);
    }
  }

  #handleQuickView() {
    // Dispatch custom event to trigger quick view modal opened by a parent/global component
    document.dispatchEvent(new CustomEvent('quickview:open', {
      bubbles: true,
      detail: {
        productUrl: this.productUrl,
        productId: this.productId
      }
    }));
  }

  #setupIntersectionObserver() {
    if (!this.#elements.badge || !('IntersectionObserver' in window)) return;

    this.#intersectionObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          requestAnimationFrame(() => {
            this.#elements.badge.classList.add('is-visible');
          });
          // Unobserve after the first intersection to run animation only once
          observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '0px',
      threshold: 0.1
    });

    this.#intersectionObserver.observe(this);
  }

  /**
   * Utility to debounce a function call
   * @param {Function} func 
   * @param {number} wait 
   * @returns {Function}
   */
  #debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Register the custom element
customElements.define('product-card', ProductCard);

// DAEYL: eyewear cards (media has data-fit-whole, i.e. product tagged Sunglasses) show square / landscape
// photos whole instead of cropping the lenses. Every other card keeps the full-bleed cover crop, including
// after a swatch click swaps in a square variant photo. Runs on every card image load (swatch/hover/ajax).
(() => {
  const RATIO = 0.85;
  const edgeColour = (img) => {
    try {
      const c = document.createElement('canvas');
      c.width = c.height = 16;
      const x = c.getContext('2d', { willReadFrequently: true });
      x.drawImage(img, 0, 0, 16, 16);
      const px = [[0, 0], [15, 0], [0, 15], [15, 15], [8, 0], [8, 15]].map(([a, b]) => x.getImageData(a, b, 1, 1).data);
      const avg = [0, 1, 2].map((i) => Math.round(px.reduce((t, p) => t + p[i], 0) / px.length));
      return `rgb(${avg.join(',')})`;
    } catch (e) {
      return '';
    }
  };
  const fit = (img) => {
    if (!img.naturalWidth || !img.naturalHeight) return;
    const box0 = img.closest('.product-card__media');
    if (!box0 || !box0.hasAttribute('data-fit-whole')) {
      img.classList.remove('product-card__img--contain');
      if (box0) box0.style.backgroundColor = '';
      return;
    }
    const contain = img.naturalWidth / img.naturalHeight > RATIO;
    img.classList.toggle('product-card__img--contain', contain);
    if (!img.classList.contains('product-card__img--primary')) return;
    const box = img.closest('.product-card__media');
    if (box) box.style.backgroundColor = contain ? edgeColour(img) : '';
  };
  document.addEventListener('load', (e) => {
    const t = e.target;
    if (t && t.tagName === 'IMG' && t.classList.contains('product-card__img')) fit(t);
  }, true);
  document.querySelectorAll('.product-card__img').forEach((img) => { if (img.complete) fit(img); });
})();
